/**
 * Client-side error type for every API failure.
 *
 * Services answer non-2xx with RFC 7807 problem documents
 * (`packages/shared/src/app-error.ts`): `{ type, title, status, detail, code,
 * …members }`, where validation failures carry `details: { field, violations }`.
 * `ApiError` normalises that — plus transport failures — into one shape the UI
 * can render as a human-readable message and an inline field error. Raw problem
 * JSON and stack traces are never shown to a user.
 */
export const CLIENT_ERROR_CODE = {
  /** fetch() rejected: offline, DNS, TLS, CORS, aborted. */
  network: 'NETWORK_UNAVAILABLE',
  /** 2xx/non-2xx body was not the JSON we expected. */
  malformed: 'MALFORMED_RESPONSE',
} as const;

export interface ApiErrorInit {
  status: number;
  code: string;
  message: string;
  field?: string | null;
  violations?: readonly string[];
  requestId?: string | null;
  /**
   * The raw RFC 7807 `details` member, untouched. Kept because some codes carry
   * structured data a form error cannot express — a 402 `TIER_LIMIT_EXCEEDED`
   * reports `{ limit_key, limit, current, minimum_plan, … }` and the upgrade CTA
   * renders those numbers verbatim.
   */
  details?: Record<string, unknown> | null;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly field: string | null;
  readonly violations: readonly string[];
  readonly requestId: string | null;
  readonly details: Record<string, unknown> | null;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.field = init.field ?? null;
    this.violations = init.violations ?? [];
    this.requestId = init.requestId ?? null;
    this.details = init.details ?? null;
  }

  static isApiError(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }

  /** True when the transport failed rather than the server answering. */
  get isOffline(): boolean {
    return this.code === CLIENT_ERROR_CODE.network;
  }
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * Pull `{ field, violations }` out of an RFC 7807 `details` member.
 *
 * Two spellings are in the wild and both are honoured: `{ field, violations }`
 * (identity/roster services) and `{ fields: [{ field, message }] }` — the shape
 * plays-service uses for a 422 (`throwValidationError`). The first entry of
 * `fields` becomes the focus target; every message becomes a violation, so a
 * form can render each one against its own control.
 */
function readDetails(record: Record<string, unknown>): {
  field: string | null;
  violations: string[];
} {
  const details = record.details;
  if (typeof details !== 'object' || details === null) return { field: null, violations: [] };
  const detailRecord = details as Record<string, unknown>;
  if (Array.isArray(detailRecord.fields)) {
    const issues = detailRecord.fields.filter(
      (entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null
    );
    return {
      field: issues.length === 0 ? null : readString(issues[0] as Record<string, unknown>, 'field'),
      violations: issues
        .map((issue) => {
          const name = readString(issue, 'field');
          const message = readString(issue, 'message');
          if (message === null) return null;
          return name === null ? message : `${name}: ${message}`;
        })
        .filter((entry): entry is string => entry !== null),
    };
  }
  const violations = Array.isArray(detailRecord.violations)
    ? detailRecord.violations.filter((entry): entry is string => typeof entry === 'string')
    : [];
  return { field: readString(detailRecord, 'field'), violations };
}

/**
 * Build an `ApiError` from a parsed error body.
 *
 * Accepts the platform's RFC 7807 documents and, defensively, the
 * `{ error: { code, message, details } }` envelope used by some upstream
 * proxies — whichever arrives, the UI sees one shape.
 */
export function toApiError(status: number, body: unknown, fallbackMessage: string): ApiError {
  if (typeof body !== 'object' || body === null) {
    return new ApiError({ status, code: `HTTP_${status}`, message: fallbackMessage });
  }
  const record = body as Record<string, unknown>;
  const nested = record.error;
  const source =
    typeof nested === 'object' && nested !== null ? (nested as Record<string, unknown>) : record;
  const { field, violations } = readDetails(source);
  return new ApiError({
    status,
    code: readString(source, 'code') ?? `HTTP_${status}`,
    message:
      readString(source, 'detail') ??
      readString(source, 'message') ??
      readString(source, 'title') ??
      fallbackMessage,
    field,
    violations,
    details:
      typeof source.details === 'object' &&
      source.details !== null &&
      !Array.isArray(source.details)
        ? (source.details as Record<string, unknown>)
        : null,
    requestId: readString(source, 'instance') ?? readString(record, 'trace_id'),
  });
}

/**
 * Codes the gateway / auth-service use for a problem with the CALLER's
 * credential — a missing, expired, revoked or otherwise unusable bearer token —
 * as opposed to something the user typed. Their `detail` strings
 * ("This endpoint requires a bearer token.") describe plumbing and must never
 * be painted into a form.
 */
const CREDENTIAL_ERROR_CODES: ReadonlySet<string> = new Set([
  'UNAUTHORIZED',
  'HTTP_401',
  'TOKEN_EXPIRED',
  'INVALID_TOKEN',
  'SESSION_NOT_FOUND',
  'SESSION_REVOKED',
]);

/** Words that mark a `detail` as written for an engineer, not a user. */
const IMPLEMENTATION_DETAIL = /\b(bearer|jwt|endpoint|header|upstream|stack|null|undefined)\b/i;

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please sign in again.';

/**
 * True when the failure is about the caller's session/token rather than the
 * request contents — i.e. the user needs to sign in again, whatever the body
 * said. A 401 with a domain code (e.g. `MFA_INVALID_CODE`, a wrong
 * authenticator code) is NOT a session error.
 */
export function isSessionError(error: unknown): boolean {
  if (!ApiError.isApiError(error)) return false;
  if (error.status !== 401 && error.status !== 403) return false;
  return CREDENTIAL_ERROR_CODES.has(error.code);
}

/**
 * The message a user may see for `error`. Offline and domain messages pass
 * through; session/token failures become `SESSION_EXPIRED_MESSAGE`; anything
 * that reads like an implementation detail — or is not an `ApiError` at all —
 * becomes `fallback`.
 */
export function toUserMessage(error: unknown, fallback: string): string {
  if (!ApiError.isApiError(error)) return fallback;
  if (error.isOffline) return error.message;
  if (isSessionError(error)) return SESSION_EXPIRED_MESSAGE;
  if (error.message === '' || IMPLEMENTATION_DETAIL.test(error.message)) return fallback;
  return error.message;
}

/** Message shown when the transport itself failed. */
export const OFFLINE_MESSAGE =
  'Cannot reach Gridiron Tracker. Check your connection — your work is kept locally until it can be sent.';

export function toNetworkError(): ApiError {
  return new ApiError({
    status: 0,
    code: CLIENT_ERROR_CODE.network,
    message: OFFLINE_MESSAGE,
  });
}
